import { createContext, useContext, useMemo } from "react"
import { resolveRoomColor, roomInactiveColor } from "config/roomColors"

// Provides a per-room highlight color to session cards and the session detail without prop
// drilling through the SessionList → TimeGroupedSessions → SessionCard tree. The parent builds
// the venueId → color map once (buildRoomColorMap) and passes it in as `colorMap`.
//
// `active` reflects the exclusive color-coding mode (see colorCoding.js): the room set is colored
// only when coloring "byRoom". When inactive, the getter returns the base blue accent so cards and
// the detail revert to the original redesign look, and `active` is exposed so the one element that
// can't express "off" as a color swap — the card title (uncolored/dark when off) — can branch.
const RoomColorContext = createContext({ getRoomColor: () => roomInactiveColor, active: false })

export function RoomColorProvider({ colorMap = {}, active = true, children }) {
    const value = useMemo(
        () => ({
            active,
            getRoomColor: venue => (active ? resolveRoomColor(colorMap, venue) : roomInactiveColor),
        }),
        [colorMap, active],
    )

    return <RoomColorContext.Provider value={value}>{children}</RoomColorContext.Provider>
}

// Returns the highlight color for a session's venue. Safe to call outside a provider (returns
// the base accent) so SessionDetail can be reused elsewhere without wiring.
export function useRoomColor(venue) {
    return useContext(RoomColorContext).getRoomColor(venue)
}

// Whether color-coding is currently "byRoom". Used by the card title, which is left uncolored
// (dark) when off rather than taking the base accent color.
export function useRoomColorActive() {
    return useContext(RoomColorContext).active
}
